import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useAdmin } from '../contexts/AdminContext';
import { adminService } from '../services/adminService';
import UserList from './admin/UserList';
import OrganizationList from './admin/OrganizationList';

const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, adminLoading, adminError } = useAdmin();

  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    // Fetch analytics when admin access is confirmed
    const fetchAnalytics = async () => {
      if (isAdmin && !adminLoading) {
        try {
          const analyticsData = await adminService.getAnalytics();
          setAnalytics(analyticsData);
        } catch (error) {
          console.error('Failed to fetch analytics:', error);
        }
      }
    };

    fetchAnalytics();
  }, [isAdmin, adminLoading]);

  // Show loading while checking authentication
  if (authLoading || adminLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: { xs: 8, md: 12 }, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Checking admin access...
        </Typography>
      </Container>
    );
  }

  // Show error if not admin
  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ mt: { xs: 8, md: 12 } }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1">
            {adminError || 'You do not have administrator access to this system.'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Contact your system administrator if you believe this is an error.
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 4, md: 8 }, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontFamily: 'Bebas Neue',
            fontWeight: 'normal',
            color: '#4b0000',
            mb: 2
          }}
        >
          ADMIN DASHBOARD
        </Typography>


        {analytics && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" component="div">
                    Total Users
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {analytics.total_users}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" component="div">
                    Sessions
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {analytics.total_sessions}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" component="div">
                    Questions Answered
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {analytics.total_responses}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

      </Box>

      <Box sx={{ mt: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontFamily: 'Bebas Neue',
              fontWeight: 'normal',
              fontSize: '1.1rem',
              minHeight: '48px'
            }
          }}
        >
          <Tab label="Users" />
          <Tab label="Organisations" />
        </Tabs>

        <Box sx={{ mt: 3 }}>
          {activeTab === 0 && <UserList />}
          {activeTab === 1 && <OrganizationList />}
        </Box>
      </Box>
    </Container>
  );
};

export default AdminDashboard;